import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "icon", "onClose", "onCloseComplete", "onOpenComplete", "overrides", "testId", "in"],
    _excluded2 = ["component"],
    _excluded3 = ["component"];

/** @jsx jsx */
import { useCallback, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import ArrowLeft from '@atlaskit/icon/glyph/arrow-left';
import { ExitingPersistence, SlideIn, useExitingPersistence } from '@atlaskit/motion';
import { N0 } from '@atlaskit/theme/colors';
import { gridSize, layers } from '@atlaskit/theme/constants';
import { animationTimingFunction, transitionDurationMs } from '../../constants';
import { createExtender } from '../utils';
import ContentOverrides from './content';
import IconButton from './icon-button';
import SidebarOverrides from './sidebar'; // Misc.
// ------------------------------

var widths = {
  full: '100vw',
  extended: '95vw',
  narrow: 45 * gridSize(),
  medium: 60 * gridSize(),
  wide: 75 * gridSize()
};
var wrapperStyles = css({
  display: 'flex',
  height: '100vh',
  position: 'fixed',
  zIndex: layers.blanket() + 1,
  top: 0,
  left: 0,
  backgroundColor: "var(--ds-surface-overlay, ".concat(N0, ")"),
  overflow: 'hidden'
});
var defaults = {
  Sidebar: SidebarOverrides,
  Content: ContentOverrides
};
/**
 * This wrapper is used to specify separate durations for enter and exit.
 */

var CustomSlideIn = function CustomSlideIn(_ref) {
  var children = _ref.children,
      onFinish = _ref.onFinish;

  var _useExitingPersistenc = useExitingPersistence(),
      isExiting = _useExitingPersistenc.isExiting;
  /**
   * The actual duration should be the same for both enter and exit,
   * but motion halves the passed duration for exit animations,
   * so we double it when exiting.
   */


  var duration = isExiting ? transitionDurationMs * 2 : transitionDurationMs;
  return jsx(SlideIn, {
    animationTimingFunction: animationTimingFunction,
    duration: duration,
    enterFrom: "left",
    exitTo: "left",
    fade: "none",
    onFinish: onFinish
  }, children);
};

var DrawerPrimitive = function DrawerPrimitive(_ref2) {
  var children = _ref2.children,
      Icon = _ref2.icon,
      onClose = _ref2.onClose,
      onCloseComplete = _ref2.onCloseComplete,
      onOpenComplete = _ref2.onOpenComplete,
      overrides = _ref2.overrides,
      testId = _ref2.testId,
      isOpen = _ref2.in,
      props = _objectWithoutProperties(_ref2, _excluded);

  var getOverrides = createExtender(defaults, overrides);

  var _getOverrides = getOverrides('Sidebar'),
      Sidebar = _getOverrides.component,
      sideBarOverrides = _objectWithoutProperties(_getOverrides, _excluded2);

  var _getOverrides2 = getOverrides('Content'),
      Content = _getOverrides2.component,
      contentOverrides = _objectWithoutProperties(_getOverrides2, _excluded3);

  var ref = useRef(null);
  var onFinish = useCallback(function (state) {
    if (state === 'entering') {
      onOpenComplete === null || onOpenComplete === void 0 ? void 0 : onOpenComplete(ref.current);
    } else if (state === 'exiting') {
      onCloseComplete === null || onCloseComplete === void 0 ? void 0 : onCloseComplete(ref.current);
    }
  }, [onCloseComplete, onOpenComplete]);
  return jsx(ExitingPersistence, {
    appear: true
  }, isOpen && jsx(CustomSlideIn, {
    onFinish: onFinish
  }, function (_ref3) {
    var _props$width;

    var className = _ref3.className;
    return jsx("div", {
      className: className,
      css: wrapperStyles,
      style: {
        width: widths[(_props$width = props.width) !== null && _props$width !== void 0 ? _props$width : 'narrow']
      },
      "data-testid": testId,
      ref: ref
    }, jsx(Sidebar, sideBarOverrides, jsx(IconButton, {
      onClick: onClose,
      testId: testId && 'DrawerPrimitiveSidebarCloseButton'
    }, Icon ? jsx(Icon, {
      size: "large"
    }) : jsx(ArrowLeft, {
      label: "Close drawer"
    }))), jsx(Content, contentOverrides, children));
  }));
};

export default DrawerPrimitive;